import { useEffect, useRef, useState, useCallback } from 'react'
import { useGetLessonBlocksQuery } from '@/features/editor/lessonsApi'
import {
  findEmptyVideoBlocks,
  type EmptyVideoLocation,
} from '@/features/editor/lib/emptyVideoBlocks'
import type { Lesson } from '@/types/lesson'

export interface EmptyVideoGateState {
  isLoading: boolean
  /**
   * Важно (ревью Task 3): отказ запроса блоков урока — ОТДЕЛЬНОЕ состояние
   * от «ещё грузится». Без него `isLoading` зависал бы `true` навсегда на
   * сетевой ошибке, без объяснения и без выхода (безопасная сторона отказа,
   * но нечестная — см. `EmptyVideoStatus`).
   */
  isError: boolean
  empties: EmptyVideoLocation[]
  /** Перечитать блоки урока(ов), упавших с ошибкой. */
  retry: () => void
}

interface RowResult {
  isLoaded: boolean
  isError: boolean
  empties: EmptyVideoLocation[]
  refetch: () => void
}

interface RowProps {
  lesson: Omit<Lesson, 'blocks'>
  /** Позиция урока в списке курса — плашка урока считает так же, а `order`
   *  идёт 0-based и с дырами (см. `EmptyVideoLocation`). */
  rank: number
  onResult: (lessonId: number, result: RowResult) => void
}

/**
 * Подписка на блоки ОДНОГО урока — хуки нельзя звать в цикле по урокам
 * (тот же приём, что `JobPoller`/`LessonTargetRow`). Ничего не рисует,
 * только сообщает результат наверх.
 */
const LessonVideoProbe = ({ lesson, rank, onResult }: RowProps) => {
  const { data: blocks, isError, refetch } = useGetLessonBlocksQuery(lesson.id)
  const empties = blocks ? findEmptyVideoBlocks([{ id: lesson.id, rank, blocks }]) : []
  // Сигнатура строкой — массивы пересоздаются на каждый рендер, по ссылке
  // эффект бил бы бесконечно (тот же приём, что в `LessonTargetRow`).
  const signature = `${Boolean(blocks)}|${isError}|${rank}|${empties.map((e) => e.blockRank).join(',')}`
  useEffect(() => {
    onResult(lesson.id, { isLoaded: Boolean(blocks), isError, empties, refetch })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])
  return null
}

interface Props {
  lessons: Omit<Lesson, 'blocks'>[]
  onChange: (state: EmptyVideoGateState) => void
}

/**
 * Гейт публикации (п.19): собирает видео-блоки без ролика по ВСЕМ урокам
 * курса. Пока блоки хоть одного урока не загрузились — `isLoading: true`
 * («не знаем» ≠ «пусто», кнопка публикации остаётся погашенной).
 */
export const EmptyVideoGate = ({ lessons, onChange }: Props) => {
  const [byLesson, setByLesson] = useState<Record<number, RowResult>>({})
  // Ref, а не замыкание на `byLesson`: `retry` живёт в состоянии родителя
  // (`PublishDialog`) и обязан звать `refetch` САМЫХ СВЕЖИХ строк, а не тех,
  // что были на момент последнего пересчёта `onChange`.
  const byLessonRef = useRef(byLesson)
  byLessonRef.current = byLesson

  const handleResult = (lessonId: number, result: RowResult) =>
    setByLesson((prev) => ({ ...prev, [lessonId]: result }))

  const retry = useCallback(() => {
    for (const result of Object.values(byLessonRef.current)) result.refetch()
  }, [])

  const isLoading = lessons.some((l) => !byLesson[l.id]?.isLoaded)
  const isError = lessons.some((l) => byLesson[l.id]?.isError)
  const empties = lessons.flatMap((l) => byLesson[l.id]?.empties ?? [])
  const signature = `${isLoading}|${isError}|${empties.map((e) => `${e.lessonRank}:${e.blockRank}`).join(',')}`

  useEffect(() => {
    onChange({ isLoading, isError, empties, retry })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])

  return (
    <>
      {lessons.map((lesson, rank) => (
        <LessonVideoProbe key={lesson.id} lesson={lesson} rank={rank} onResult={handleResult} />
      ))}
    </>
  )
}
